import { startOfDay } from 'date-fns';

export const formatAvailabilityDateTimeParam = (timestamp, time = '00:00') => {
  const date = startOfDay(new Date(timestamp));
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const [hours = '00', minutes = '00'] = String(time || '00:00').split(':');

  return `${year}-${month}-${day}T${hours.padStart(2, '0')}:${minutes.padStart(
    2,
    '0'
  )}:00.000Z`;
};

export const buildPeriodRequestParams = ({
  dateFrom,
  dateTo,
  timeFrom = '00:00',
  timeTo = '00:00',
}) => {
  if (!dateFrom || !dateTo) {
    return {};
  }

  const endTime = timeTo === '00:00' ? '23:59' : timeTo;

  return {
    date_from: formatAvailabilityDateTimeParam(dateFrom, timeFrom),
    date_to: formatAvailabilityDateTimeParam(dateTo, endTime),
  };
};

export const getFaultyPercent = (faultyCount, totalCount) =>
  totalCount > 0 ? Math.round((faultyCount / totalCount) * 10000) / 100 : 0;

export const buildAvailabilityChartData = (faultyCount, totalCount) => {
  const faultyPercent = getFaultyPercent(faultyCount, totalCount);

  return [
    {
      name: 'Nepasiekiamas',
      value: faultyPercent,
      fill: 'hsl(0 84% 60%)',
      isFaulty: true,
    },
    {
      name: 'Pasiekiamas',
      value: 100 - faultyPercent,
      fill: 'hsl(142 76% 36%)',
    },
  ];
};

const EMPTY_CHART_DATA = buildAvailabilityChartData(0, 0);

export const buildDefaultDeviceStatistics = () => [
  {
    title: 'MQTT Brokerio serverio pasiekiamumas',
    subTitle: '24 valandų pasiekiamumas',
    chartData: EMPTY_CHART_DATA,
  },
  {
    title: 'MQTT prietaisų statusas',
    subTitle: 'Viso: 0 prietaisų',
    chartData: EMPTY_CHART_DATA,
    chartLabel: '0% prietaisų buvo nepasiekiami',
  },
  {
    title: '2G prietaisų statusas',
    subTitle: 'Viso: 0 prietaisų · aktyvūs: 0',
    chartData: EMPTY_CHART_DATA,
    chartLabel: '0% prietaisų buvo nepasiekiami',
  },
];

export const buildAvailabilityChartDataFromPercent = faultyPercent => [
  {
    name: 'Nepasiekiamas',
    value: faultyPercent,
    fill: 'hsl(0 84% 60%)',
    isFaulty: true,
  },
  {
    name: 'Pasiekiamas',
    value: 100 - faultyPercent,
    fill: 'hsl(142 76% 36%)',
  },
];

export const buildDeviceStatistics = availabilityDevices => {
  if (!availabilityDevices) {
    return null;
  }

  const hasCountFields =
    availabilityDevices.http_faulty_device_count !== undefined ||
    availabilityDevices.mqtt_faulty_device_count !== undefined ||
    availabilityDevices.http_active_device_count !== undefined ||
    availabilityDevices.mqtt_active_device_count !== undefined;

  if (hasCountFields) {
    return buildDeviceStatisticsFromCounts(availabilityDevices);
  }

  return buildDeviceStatisticsFromPercents(availabilityDevices);
};

const buildDeviceStatisticsFromCounts = availabilityDevices => {
  const {
    http_device_count: httpDeviceCount,
    http_faulty_device_count: httpFaultyDeviceCount,
    http_active_device_count: httpActiveDeviceCount,
    http_faulty_device_ids: httpFaultyDeviceIds = [],
    mqtt_device_count: mqttDeviceCount,
    mqtt_faulty_device_count: mqttFaultyDeviceCount,
    mqtt_faulty_device_ids: mqttFaultyDeviceIds = [],
  } = availabilityDevices;

  const mqttFaultyPercent = getFaultyPercent(
    mqttFaultyDeviceCount,
    mqttDeviceCount
  );
  const httpFaultyPercent = getFaultyPercent(
    httpFaultyDeviceCount,
    httpDeviceCount
  );

  const brokerData = buildAvailabilityChartData(
    mqttFaultyDeviceCount,
    mqttDeviceCount
  );
  const mqttDevicesData = buildAvailabilityChartData(
    mqttFaultyDeviceCount,
    mqttDeviceCount
  );
  const twoGData = buildAvailabilityChartData(
    httpFaultyDeviceCount,
    httpDeviceCount
  );

  return [
    {
      title: 'MQTT Brokerio serverio pasiekiamumas',
      subTitle: '24 valandų pasiekiamumas',
      chartData: brokerData,
      faultyDeviceIds: mqttFaultyDeviceIds,
    },
    {
      title: 'MQTT prietaisų statusas',
      subTitle: `Viso: ${mqttDeviceCount} prietaisų`,
      chartData: mqttDevicesData,
      chartLabel: `${mqttFaultyPercent}% prietaisų buvo nepasiekiami`,
      faultyDeviceIds: mqttFaultyDeviceIds,
    },
    {
      title: '2G prietaisų statusas',
      subTitle: `Viso: ${httpDeviceCount} prietaisų · aktyvūs: ${httpActiveDeviceCount}`,
      chartData: twoGData,
      chartLabel: `${httpFaultyPercent}% prietaisų buvo nepasiekiami`,
      faultyDeviceIds: httpFaultyDeviceIds,
    },
  ];
};

const buildDeviceStatisticsFromPercents = availabilityDevices => {
  const {
    http_device_count: httpDeviceCount = 0,
    mqtt_device_count: mqttDeviceCount = 0,
    http_devices_with_issues_percent: httpFaultyPercent = 0,
    mqtt_devices_with_issues_percent: mqttFaultyPercent = 0,
    mqtt_broker_unavailable_percent: brokerUnavailablePercent = 0,
    http_faulty_device_ids: httpFaultyDeviceIds = [],
    mqtt_faulty_device_ids: mqttFaultyDeviceIds = [],
  } = availabilityDevices;

  return [
    {
      title: 'MQTT Brokerio serverio pasiekiamumas',
      subTitle: '24 valandų pasiekiamumas',
      chartData: buildAvailabilityChartDataFromPercent(
        brokerUnavailablePercent
      ),
      faultyDeviceIds: mqttFaultyDeviceIds,
    },
    {
      title: 'MQTT prietaisų statusas',
      subTitle: `Viso: ${mqttDeviceCount} prietaisų`,
      chartData: buildAvailabilityChartDataFromPercent(mqttFaultyPercent),
      chartLabel: `${mqttFaultyPercent}% prietaisų buvo nepasiekiami`,
      faultyDeviceIds: mqttFaultyDeviceIds,
    },
    {
      title: '2G prietaisų statusas',
      subTitle: `Viso: ${httpDeviceCount} prietaisų`,
      chartData: buildAvailabilityChartDataFromPercent(httpFaultyPercent),
      chartLabel: `${httpFaultyPercent}% prietaisų buvo nepasiekiami`,
      faultyDeviceIds: httpFaultyDeviceIds,
    },
  ];
};
